import { useContext } from "react";
import { ValidationResult } from "sprint-common-web";

import { AppConfigContext } from "../../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../../core/data-access/useAuthorizedFetch";
import { AbandonAllCloudFilesResponse } from "../cloud-files/abandon/AbandonAllCloudFilesResponse";
import { AbandonCloudFileRequest } from "../cloud-files/abandon/AbandonCloudFileRequest";
import { AbandonCloudFileResponse } from "../cloud-files/abandon/AbandonCloudFileResponse";
import { AbandonOrphanedCloudFilesResponse } from "../cloud-files/abandon/AbandonOrphanedCloudFilesResponse";
import { RequestCloudFileCloudFixRequest } from "../cloud-files/cloud-fix/request/RequestCloudFileCloudFixRequest";
import { ValidateRequestCloudFileCloudFixRequest } from "../cloud-files/cloud-fix/validate/ValidateRequestCloudFileCloudFixRequest";
import { GetCloudFileListRequest } from "../cloud-files/get-list/GetCloudFileListRequest";
import { GetCloudFileListResponse } from "../cloud-files/get-list/GetCloudFileListResponse";
import { GetCloudFileStatusDetailsRequest } from "../cloud-files/get-status/GetCloudFileStatusDetailsRequest";
import { GetCloudFileStatusDetailsResponse } from "../cloud-files/get-status/GetCloudFileStatusDetailsResponse";
import { RejectCloudFileRequest } from "../cloud-files/reject/RejectCloudFileRequest";
import { RepeatCloudFileProcessingRequest } from "../cloud-files/repeat-processing/RepeatCloudFileProcessingRequest";
import { ValidateIfObjectExistsRequest } from "../qc/ValidateIfObjectExistsRequest";
import { ApproveCloudFileNotificationsRequest } from "./approve-notifications/ApproveCloudFileNotificationsRequest";
import { AssignFileRecordToCloudFileRequest } from "./assign-file-record/AssignFileRecordToCloudFileRequest";
import { CopyCloudFileToUploadRequest } from "./copy-to-upload/CopyCloudFileToUploadRequest";
import { GetCloudFileDetailsRequest } from "./get-details/GetCloudFileDetailsRequest";
import { GetCloudFileDetailsResponse } from "./get-details/GetCloudFileDetailsResponse";
import { GetCloudFilePreviewRequest } from "./get-preview/GetCloudFilePreviewRequest";
import { GetCloudFilePreviewResponse } from "./get-preview/GetCloudFilePreviewResponse";
import { GetCloudFileProxiesRequest } from "./get-proxies/GetCloudFileProxiesRequest";
import { GetCloudFileProxiesResponse } from "./get-proxies/GetCloudFileProxiesResponse";
import { GetRekognitionResultRequest } from "./get-rekognition-result/GetRekognitionResultRequest";
import { GetRekognitionResultResponse } from "./get-rekognition-result/GetRekognitionResultResponse";
import { OverwriteRekognitionSegmentRequest } from "./overwrite-rekognition-segment/OverwriteRekognitionSegmentRequest";
import { CloudFileQcDoneRequest } from "./qc-done/CloudFileQcDoneRequest";
import { RequestWaitingForClientResponseRequest } from "./request-waiting-for-client-response/RequestWaitingForClientResponseRequest";
import { UpdateAudioCloudFileRequest } from "./update/UpdateAudioCloudFileRequest";
import { UpdateSubtitleCloudFileRequest } from "./update/UpdateSubtitleCloudFileRequest";
import { UpdateVideoCloudFileRequest } from "./update/UpdateVideoCloudFileRequest";
import { ValidateCloudFileToQcDoneRequest } from "./validate-to-qc-done/ValidateCloudFileToQcDoneRequest";

export const useCloudFileApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const baseUrl = `${appConfigContext.masterConfig.apiUrl}/cloud-files`;

    const { httpGet, httpPut, httpPost, httpDelete } = useAuthorizedFetch();

    const getCloudFileDetails = async (query: GetCloudFileDetailsRequest): Promise<GetCloudFileDetailsResponse> => {
        return httpGet<GetCloudFileDetailsResponse>(`${baseUrl}/get-details`, query);
    };

    const getCloudFilePreview = async (query: GetCloudFilePreviewRequest): Promise<GetCloudFilePreviewResponse> => {
        return httpGet<GetCloudFilePreviewResponse>(`${baseUrl}/get-preview`, query);
    };

    const getRekognitionResult = async (query: GetRekognitionResultRequest): Promise<GetRekognitionResultResponse> => {
        return httpGet<GetRekognitionResultResponse>(`${baseUrl}/get-rekognition-result`, query);
    };

    const overwriteRekognitionSegment = async (query: OverwriteRekognitionSegmentRequest): Promise<void> => {
        return httpPut(`${baseUrl}/overwrite-rekognition-segment`, query);
    };

    const getCloudFileList = async (query: GetCloudFileListRequest): Promise<GetCloudFileListResponse> => {
        return httpGet<GetCloudFileListResponse>(`${baseUrl}/get-list`, query);
    };

    const getCloudFileProxies = async (query: GetCloudFileProxiesRequest): Promise<GetCloudFileProxiesResponse> => {
        return httpGet<GetCloudFileProxiesResponse>(`${baseUrl}/get-proxies`, query);
    };

    const getCloudFileStatus = async (
        request: GetCloudFileStatusDetailsRequest
    ): Promise<GetCloudFileStatusDetailsResponse> => {
        return httpGet<GetCloudFileStatusDetailsResponse>(`${baseUrl}/status`, request);
    };

    const rejectCloudFile = async (request: RejectCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/reject`, request);
    };

    const repeatCloudFileProcessing = async (request: RepeatCloudFileProcessingRequest): Promise<void> => {
        return httpPut(`${baseUrl}/repeat-processing`, request);
    };

    const abandonCloudFile = async (request: AbandonCloudFileRequest): Promise<AbandonCloudFileResponse> => {
        return httpDelete(`${baseUrl}/abandon`, request);
    };

    const abandonAllCloudFiles = async (): Promise<AbandonAllCloudFilesResponse> => {
        return httpDelete(`${baseUrl}/abandon-all`);
    };

    const abandonOrphanedCloudFiles = async (): Promise<AbandonOrphanedCloudFilesResponse> => {
        return httpDelete(`${baseUrl}/abandon-orphaned`);
    };

    const approveCloudFileNotifications = async (request: ApproveCloudFileNotificationsRequest): Promise<void> => {
        return httpPut(`${baseUrl}/approve-notifications`, request);
    };

    const requestCloudFix = async (request: RequestCloudFileCloudFixRequest): Promise<void> => {
        return httpPost(`${baseUrl}/request-cloud-fix`, request);
    };

    const validateRequestCloudFix = async (
        request: ValidateRequestCloudFileCloudFixRequest
    ): Promise<ValidationResult> => {
        return httpPost(`${baseUrl}/validate-cloud-fix`, request);
    };

    const copyCloudFileToUpload = async (request: CopyCloudFileToUploadRequest): Promise<void> => {
        return httpPost(`${baseUrl}/copy-to-upload`, request);
    };

    const validateCloudFileToQcDone = async (request: ValidateCloudFileToQcDoneRequest): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/validate-to-qc-done`, request);
    };

    const validateIfObjectExists = async (
        request: ValidateIfObjectExistsRequest,
        signal?: AbortSignal
    ): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(
            `${appConfigContext.masterConfig.apiUrl}/validate-if-object-exists`,
            request,
            signal
        );
    };

    const assignFileRecordToCloudFile = async (request: AssignFileRecordToCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/assign-file-record`, request);
    };

    const cloudFileQcDone = async (request: CloudFileQcDoneRequest): Promise<void> => {
        return httpPut(`${baseUrl}/qc-done`, request);
    };

    const updateVideoCloudFile = async (request: UpdateVideoCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-video`, request);
    };

    const updateAudioCloudFile = async (request: UpdateAudioCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-audio`, request);
    };

    const updateSubtitleCloudFile = async (request: UpdateSubtitleCloudFileRequest): Promise<void> => {
        return httpPut(`${baseUrl}/update-subtitle`, request);
    };

    const requestFilesWaitingForClientResponse = async (
        request: RequestWaitingForClientResponseRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/request-waiting-for-client-response`, request);
    };

    return {
        getCloudFileDetails,
        getCloudFilePreview,
        getRekognitionResult,
        overwriteRekognitionSegment,
        getCloudFileList,
        getCloudFileProxies,
        rejectCloudFile,
        getCloudFileStatus,
        repeatCloudFileProcessing,
        abandonCloudFile,
        abandonAllCloudFiles,
        abandonOrphanedCloudFiles,
        requestCloudFix,
        validateRequestCloudFix,
        copyCloudFileToUpload,
        validateIfObjectExists,
        validateCloudFileToQcDone,
        approveCloudFileNotifications,
        assignFileRecordToCloudFile,
        cloudFileQcDone,
        updateVideoCloudFile,
        updateAudioCloudFile,
        updateSubtitleCloudFile,
        requestFilesWaitingForClientResponse
    };
};
